import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Timestamp } from 'firebase/firestore';
import {
    CheckEmailStatusResponse,
    DisplayValue,
    OrgDraftPart,
    StaffResolutionStatus,
    StaffRole,
    StaffRow,
} from '@newgenus/common';
import { ORG_DRAFT_MAX_STAFF, OrgWizardStateService } from '../services/org-wizard-state.service';
import { OrgCreationService } from '../services/org-creation.service';

/**
 * Step 4 — Staff table.
 *
 * Plan reference: §4.1 Step 4, §16.4, §10 ("Step 4 staff table").
 *
 * P2 scope:
 *  - Add / remove / inline-edit staff rows (email + optional first/last names).
 *  - Debounced (300 ms after email change) `checkEmail` resolution. The
 *    wizard's draft has no real `orgKey` yet so we pass the `draftKey` —
 *    the backend's `isUserAlreadyMember` and pending-invitation queries
 *    safely fall through to `false`, and we only consume `existsInAuth`.
 *  - Per-row badge: ✅ existing user / ✉️ will be invited / ⚠️ invalid.
 *  - Per-row "Assign to post" picker (mat-select) sourced from the
 *    `OrgDraft.structure`'s `post` rows.
 *  - Per-row role select (owner / manager / member). Default = `member`.
 *  - Bulk paste textarea ("one email per line, optional `Name <email>`").
 *  - Cap counter against `ORG_DRAFT_MAX_STAFF` with a red warn variant.
 *
 * Notes:
 *  - The CEO/owner (the wizard user) is **always** seeded by the backend
 *    `createFromDraft` orchestrator — we do not surface a row for them here.
 *  - "Drag staff onto post" (plan §10) is deferred to P3 alongside the Excel
 *    importer; the mat-select picker is the MVP equivalent and is much more
 *    robust on touch / keyboard-only setups.
 */
@Component({
    selector: 'newgenus-org-creation-step-staff',
    templateUrl: './step-4-staff.component.html',
    styleUrls: ['./step-4-staff.component.scss'],
    standalone: false,
})
export class StepStaffComponent implements OnInit, OnDestroy {
    public state = inject(OrgWizardStateService);
    private orgCreation = inject(OrgCreationService);

    public readonly maxStaff = ORG_DRAFT_MAX_STAFF;
    public readonly roles: ReadonlyArray<StaffRole> = ['owner', 'manager', 'member'];

    /** `rowId` of a staff row currently in inline-edit mode for the email field. */
    public editingEmailRowId: string | null = null;
    public editingEmailDraft = '';

    /** Bulk paste textarea content. */
    public bulkText = '';
    public showBulkPaste = false;
    public bulkFeedback: string | null = null;

    /** Per-row debounce streams. Keyed by rowId so editing one doesn't reset another. */
    private emailInput$ = new Subject<{ rowId: string; email: string }>();
    private subs: Subscription[] = [];

    public ngOnInit(): void {
        // Per-row debounced email resolver.
        this.subs.push(
            this.emailInput$
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged((a, b) => a.rowId === b.rowId && a.email === b.email)
                )
                .subscribe(({ rowId, email }) => this.resolveEmail(rowId, email))
        );

        // Legacy-draft cleanup: drafts created before the multi-post staff
        // stage existed put each post on its own row (one email repeated N
        // times). Collapse those into one row per email with the post
        // assignments unioned. Runs before email resolution so we don't fire
        // duplicate `checkEmail` calls for the rows we're about to drop.
        this.state.mergeDuplicateStaffEmails();

        // Kick off resolution for any pre-populated rows that haven't been
        // checked yet (e.g. rows added by the Excel importer or bulk paste
        // before this step was first opened). Without this the badge would
        // sit on `unresolved` until the user manually re-edits the email.
        const draft = this.state.snapshot;
        for (const row of draft?.staff ?? []) {
            if (row.resolutionStatus === 'unresolved' && row.email && this.looksLikeEmail(row.email)) {
                this.resolveEmail(row.rowId, row.email);
            }
        }
    }

    public ngOnDestroy(): void {
        this.subs.forEach((s) => s.unsubscribe());
        this.emailInput$.complete();
    }

    // ── Post picker ──────────────────────────────────────────────────────────
    public postsFrom(parts: OrgDraftPart[]): OrgDraftPart[] {
        // Only "post" type parts are valid assignment targets in v1.
        return parts.filter((p) => p.partType === 'post').sort((a, b) => a.depth - b.depth || a.id - b.id);
    }

    public postLabelFor(parts: OrgDraftPart[], postKey: string | undefined): string {
        if (!postKey) return '(unassigned)';
        const p = parts.find((x) => x.partKey === postKey);
        return p ? p.name : '(removed)';
    }

    /**
     * Per-row reactive FormControl for the searchable multi-select. Cached by
     * `rowId` so each row keeps its own control across change-detection cycles
     * (re-creating it every CD tick would clear the search box on every
     * keystroke). The control's value is the selected `postKeys` string array
     * and is kept in sync with the draft via {@link onPostKeysChange}.
     */
    private postControlsByRowId = new Map<string, FormControl<string[]>>();

    public postControlFor(row: StaffRow): FormControl<string[]> {
        let ctrl = this.postControlsByRowId.get(row.rowId);
        const desired = this.normalizePostKeys(row);
        if (!ctrl) {
            ctrl = new FormControl<string[]>(desired, { nonNullable: true });
            this.postControlsByRowId.set(row.rowId, ctrl);
        } else if (!this.arrayEquals(ctrl.value, desired)) {
            // Draft was changed externally (undo/redo, autosave round-trip,
            // bulk paste). Sync the control without firing valueChanges.
            ctrl.setValue(desired, { emitEvent: false });
        }
        return ctrl;
    }

    /**
     * Build the `DisplayValue[]` feed for the searchable multi-select from the
     * draft's structure. Memoised by reference so the searchable-select doesn't
     * thrash its internal filtered list on every change-detection tick.
     */
    private searchablePostsCache: { src: OrgDraftPart[]; out: DisplayValue[] } | null = null;
    public searchablePostsFrom(parts: OrgDraftPart[]): DisplayValue[] {
        if (this.searchablePostsCache?.src === parts) return this.searchablePostsCache.out;
        const out = this.postsFrom(parts).map<DisplayValue>((p) => ({ value: p.partKey, display: p.name }));
        this.searchablePostsCache = { src: parts, out };
        return out;
    }

    /** Handler bound to `(selected)` from the searchable-select. */
    public onPostKeysChange(row: StaffRow, _picked: DisplayValue | DisplayValue[]): void {
        const ctrl = this.postControlsByRowId.get(row.rowId);
        const next = Array.isArray(ctrl?.value) ? (ctrl!.value as string[]) : [];
        this.state.setStaffPostKeys(row.rowId, next);
    }

    /** Chip-remove handler — drops a single post from the row's selection. */
    public removePostFromRow(row: StaffRow, postKey: string): void {
        const ctrl = this.postControlFor(row);
        const next = (ctrl.value ?? []).filter((k) => k !== postKey);
        ctrl.setValue(next);
        this.state.setStaffPostKeys(row.rowId, next);
    }

    /** Chips below the selector — only includes keys that still exist in the structure. */
    public selectedPostsFor(row: StaffRow, parts: OrgDraftPart[]): DisplayValue[] {
        const keys = this.normalizePostKeys(row);
        if (!keys.length) return [];
        const lookup = new Map(parts.map((p) => [p.partKey, p.name] as const));
        return keys.filter((k) => lookup.has(k)).map((k) => ({ value: k, display: lookup.get(k) as string }));
    }

    /** Read the canonical post-key list off a row, honouring the legacy `postKey` field. */
    private normalizePostKeys(row: StaffRow): string[] {
        if (row.postKeys?.length) return [...row.postKeys];
        if (row.postKey) return [row.postKey];
        return [];
    }

    private arrayEquals(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
        const x = a ?? [];
        const y = b ?? [];
        if (x.length !== y.length) return false;
        for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
        return true;
    }

    // ── Row CRUD ─────────────────────────────────────────────────────────────
    public addRow(): void {
        this.state.addStaffRow();
    }

    public removeRow(row: StaffRow): void {
        this.postControlsByRowId.delete(row.rowId);
        this.state.removeStaffRow(row.rowId);
    }

    public trackByRowId(_i: number, row: StaffRow): string {
        return row.rowId;
    }

    public trackByValue(_i: number, item: DisplayValue): string {
        return String(item.value);
    }

    // ── Inline email editing ─────────────────────────────────────────────────
    public startEditEmail(row: StaffRow): void {
        this.editingEmailRowId = row.rowId;
        this.editingEmailDraft = row.email;
    }

    public commitEditEmail(row: StaffRow): void {
        if (this.editingEmailRowId !== row.rowId) return;
        const next = this.editingEmailDraft.trim();
        this.editingEmailRowId = null;
        if (next !== row.email) {
            this.state.updateStaffRow(row.rowId, { email: next });
            if (next && this.looksLikeEmail(next)) {
                this.emailInput$.next({ rowId: row.rowId, email: next });
            } else if (next) {
                this.state.setStaffResolution(row.rowId, {
                    resolutionStatus: 'invalid',
                    resolvedUid: undefined,
                    resolutionCheckedAt: this.now(),
                });
            }
        }
    }

    public cancelEditEmail(): void {
        this.editingEmailRowId = null;
    }

    public updateFirstName(row: StaffRow, value: string): void {
        this.state.updateStaffRow(row.rowId, { firstName: value });
    }

    public updateLastName(row: StaffRow, value: string): void {
        this.state.updateStaffRow(row.rowId, { lastName: value });
    }

    public setRole(row: StaffRow, role: StaffRole): void {
        this.state.setStaffRole(row.rowId, role);
    }

    public assignPost(row: StaffRow, postKey: string | undefined): void {
        this.state.assignStaffToPost(row.rowId, postKey || undefined);
    }

    // ── Validation gate (Phase B.2b, spec §6.5) ───────────────────────────
    /**
     * Rows whose email never resolved — the inline banner surfaces a count and
     * the wizard's Next button stays disabled until this hits zero.
     */
    public get unresolvedCount(): number {
        return (this.state.snapshot?.staff ?? []).filter((r) => r.resolutionStatus === 'unresolved').length;
    }

    /** Rows where the email parsed as obviously invalid (malformed address). */
    public get invalidCount(): number {
        return (this.state.snapshot?.staff ?? []).filter((r) => r.resolutionStatus === 'invalid').length;
    }

    public get hasUnresolvedOrInvalid(): boolean {
        return this.unresolvedCount + this.invalidCount > 0;
    }

    /** True when two or more rows share the same email — surfaces the merge button. */
    public get hasDuplicateEmails(): boolean {
        return this.state.hasDuplicateStaffEmails();
    }

    /**
     * Collapse rows that share the same email into one, unioning their
     * postKeys. Called automatically once on init (to clean up legacy drafts
     * built before the multi-post staff stage existed) and again from the
     * toolbar button if the user manages to create new duplicates by hand.
     */
    public mergeDuplicateEmails(): { collapsed: number } {
        const collapsed = this.state.mergeDuplicateStaffEmails();
        return { collapsed };
    }

    /**
     * Kick `checkEmail` for every row still sitting on `unresolved` with a
     * syntactically valid address. Called from the inline banner's "Re-check"
     * button so the user has a one-click escape hatch when the debounce timer
     * hasn't fired yet (e.g. they pasted a row and immediately hit Next).
     */
    public recheckUnresolvedRows(): void {
        const draft = this.state.snapshot;
        if (!draft) return;
        for (const row of draft.staff ?? []) {
            if (row.resolutionStatus !== 'unresolved' || !row.email) continue;
            if (this.looksLikeEmail(row.email)) {
                this.emailInput$.next({ rowId: row.rowId, email: row.email });
            }
        }
    }

    // ── Bulk paste ───────────────────────────────────────────────────────────
    public toggleBulkPaste(): void {
        this.showBulkPaste = !this.showBulkPaste;
        this.bulkFeedback = null;
    }

    public commitBulkPaste(): void {
        if (!this.bulkText.trim()) {
            this.bulkFeedback = 'Nothing to import — paste one email per line.';
            return;
        }
        const before = this.state.snapshot?.staff?.length ?? 0;
        const added = this.state.bulkAddStaffFromEmails(this.bulkText);
        const after = this.state.snapshot?.staff?.length ?? before;
        if (added === 0) {
            this.bulkFeedback = 'No new emails found (duplicates or no parsable lines).';
            return;
        }
        // Kick off resolution for each newly added row.
        const newRows = (this.state.snapshot?.staff ?? []).slice(before, after);
        for (const r of newRows) {
            if (r.email && this.looksLikeEmail(r.email)) {
                this.emailInput$.next({ rowId: r.rowId, email: r.email });
            }
        }
        this.bulkText = '';
        this.bulkFeedback = `${added} row${added === 1 ? '' : 's'} added.`;
    }

    // ── Resolution helpers ───────────────────────────────────────────────────
    public statusIcon(status: StaffResolutionStatus): string {
        switch (status) {
            case 'existing-user':
                return 'verified_user';
            case 'invitable':
                return 'mail_outline';
            case 'invalid':
                return 'error_outline';
            case 'unresolved':
            default:
                return 'help_outline';
        }
    }

    public statusLabel(status: StaffResolutionStatus): string {
        switch (status) {
            case 'existing-user':
                return 'Existing user';
            case 'invitable':
                return 'Will be invited';
            case 'invalid':
                return 'Invalid email';
            case 'unresolved':
            default:
                return 'Pending check';
        }
    }

    private resolveEmail(rowId: string, email: string): void {
        const draft = this.state.snapshot;
        if (!draft) return;
        // The row could have been removed during the debounce window.
        const row = (draft.staff ?? []).find((r) => r.rowId === rowId);
        if (!row || row.email !== email) return;

        if (!this.looksLikeEmail(email)) {
            this.state.setStaffResolution(rowId, {
                resolutionStatus: 'invalid',
                resolvedUid: undefined,
                resolutionCheckedAt: this.now(),
            });
            return;
        }

        this.orgCreation.checkEmail(email, draft.key).subscribe((res: CheckEmailStatusResponse) => {
            // Re-check that the row still exists and the email hasn't changed
            // while the request was in flight.
            const current = this.state.snapshot?.staff?.find((r) => r.rowId === rowId);
            if (!current || current.email !== email) return;
            // CRITICAL: persist `res.uid` as `resolvedUid` on the row. The
            // backend staff stage gates pendingApprovals + hat pre-stage on
            // `resolutionStatus === 'existing-user' && resolvedUid`, so a row
            // with status set but uid missing silently skips the pre-stage and
            // only creates an invitation — leaving the org board empty of
            // pending occupants on submit.
            this.state.setStaffResolution(rowId, {
                resolutionStatus: res.existsInAuth ? 'existing-user' : 'invitable',
                resolvedUid: res.existsInAuth ? res.uid : undefined,
                resolutionCheckedAt: this.now(),
                ...(res.displayName ? this.splitDisplayName(current, res.displayName) : {}),
            });
        });
    }

    private splitDisplayName(row: StaffRow, displayName: string): { firstName?: string; lastName?: string } {
        // Only auto-fill if the user hasn't typed anything in the name columns yet.
        if (row.firstName || row.lastName) return {};
        const [first = '', ...rest] = displayName.split(/\s+/).filter(Boolean);
        return { firstName: first || undefined, lastName: rest.length ? rest.join(' ') : undefined };
    }

    private looksLikeEmail(value: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    private now(): Timestamp {
        return Timestamp.now();
    }
}
